import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Filter, X } from 'lucide-react';
import { dashboardAPI } from '../services/api';
import PageOptionsButton from './PageOptionsButton';
import { bdaOptions } from '../utils/bdeOwnership';

const DailyActivityTracker = ({ selectedDate, selectedBDE, onRefreshReady, onExportExcel, onExportPDF, showExport = false }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bdeUsers, setBdeUsers] = useState([]);
  const hasLoadedRef = useRef(false);

  const [localDateFrom, setLocalDateFrom] = useState('');
  const [localDateTo, setLocalDateTo] = useState('');
  const [localBDE, setLocalBDE] = useState('All');

  useEffect(() => {
    const fetchBDEUsers = async () => {
      try {
        const response = await dashboardAPI.getBDEUsers();
        if (response && response.data) setBdeUsers(response.data);
      } catch (error) { console.error('Error fetching BDE users:', error); }
    };
    fetchBDEUsers();
  }, []);

  const fetchDailyActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getDailyActivity({
        bde: localBDE !== 'All' ? localBDE : (selectedBDE !== 'All' ? selectedBDE : undefined)
      });

      let filtered = response.data || [];
      if (localDateFrom) filtered = filtered.filter(a => a.lead_date && a.lead_date >= localDateFrom);
      if (localDateTo) filtered = filtered.filter(a => a.lead_date && a.lead_date <= localDateTo);
      setActivities(filtered);
    } catch (error) {
      console.error('Error fetching daily activities:', error);
    } finally {
      setLoading(false);
    }
  }, [localBDE, selectedBDE, localDateFrom, localDateTo]);

  useEffect(() => {
    fetchDailyActivities();
    hasLoadedRef.current = true;
  }, [fetchDailyActivities]);

  useEffect(() => {
    window.activityRefresh = fetchDailyActivities;
    if (onRefreshReady) onRefreshReady(fetchDailyActivities);
    return () => { delete window.activityRefresh; };
  }, [fetchDailyActivities, onRefreshReady]);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const yesterday = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const todayActivities = useMemo(() => activities.filter(a => a.lead_date === today), [activities, today]);
  const yesterdayActivities = useMemo(() => activities.filter(a => a.lead_date === yesterday), [activities, yesterday]);

  const sumField = (list, field) => list.reduce((s, a) => s + (parseInt(a[field]) || 0), 0);

  const todayStats = useMemo(() => ({
    leads: todayActivities.length,
    poc: sumField(todayActivities, 'poc_shortlisted'),
    emails: sumField(todayActivities, 'email_sent'),
    connections: sumField(todayActivities, 'new_connection'),
    replies: sumField(todayActivities, 'auto_reply_received'),
  }), [todayActivities]);

  const yesterdayStats = useMemo(() => ({
    leads: yesterdayActivities.length,
    poc: sumField(yesterdayActivities, 'poc_shortlisted'),
    emails: sumField(yesterdayActivities, 'email_sent'),
    connections: sumField(yesterdayActivities, 'new_connection'),
    replies: sumField(yesterdayActivities, 'auto_reply_received'),
  }), [yesterdayActivities]);

  const bdeBreakdown = useMemo(() => {
    const groups = {};
    activities.forEach(a => {
      const bde = a.bde_name || 'Unassigned';
      if (!groups[bde]) groups[bde] = { name: bde, leads: 0, poc: 0, emails: 0, connections: 0, replies: 0 };
      groups[bde].leads++;
      groups[bde].poc += parseInt(a.poc_shortlisted) || 0;
      groups[bde].emails += parseInt(a.email_sent) || 0;
      groups[bde].connections += parseInt(a.new_connection) || 0;
      groups[bde].replies += parseInt(a.auto_reply_received) || 0;
    });
    return Object.values(groups).sort((a, b) => b.leads - a.leads);
  }, [activities]);

  const dateGroups = useMemo(() => {
    const groups = {};
    activities.forEach(a => {
      const date = a.lead_date || 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(a);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 14);
  }, [activities]);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Unknown') return 'Unknown';
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const diff = (curr, prev) => {
    if (prev === 0) return curr > 0 ? '+' + curr : '';
    const d = curr - prev;
    return d > 0 ? '+' + d : d < 0 ? '' + d : '0';
  };

  const diffColor = (curr, prev) => {
    if (curr > prev) return 'text-green-600';
    if (curr < prev) return 'text-red-500';
    return 'text-gray-400';
  };

  const clearFilters = () => { setLocalDateFrom(''); setLocalDateTo(''); setLocalBDE('All'); };
  const hasFilters = localDateFrom || localDateTo || localBDE !== 'All';

  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#679632]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <Filter size={16} className="text-gray-400" />
          <input type="date" value={localDateFrom} onChange={e => setLocalDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
          <span className="text-xs text-gray-500">to</span>
          <input type="date" value={localDateTo} onChange={e => setLocalDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
          <select value={localBDE} onChange={e => setLocalBDE(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
            <option value="All">All BDEs</option>
            {bdaOptions(bdeUsers).map(bde => <option key={bde.id} value={bde.name}>{bde.name}</option>)}
          </select>
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">
              <X size={14} /> Clear
            </button>
          )}
          <div className="ml-auto text-xs text-gray-500">{activities.length} activities</div>
          <PageOptionsButton onExportExcel={onExportExcel} onExportPDF={onExportPDF} showExport={showExport} />
        </div>
      </div>

      {/* Today vs Yesterday */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { label: 'Leads Added', t: todayStats.leads, y: yesterdayStats.leads },
          { label: 'POCs Found', t: todayStats.poc, y: yesterdayStats.poc },
          { label: 'Emails Sent', t: todayStats.emails, y: yesterdayStats.emails },
          { label: 'Connections', t: todayStats.connections, y: yesterdayStats.connections },
          { label: 'Replies', t: todayStats.replies, y: yesterdayStats.replies },
        ].map(({ label, t, y }) => (
          <div key={label} className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-lg font-bold text-gray-900">{t}</span>
              <span className={`text-xs font-medium ${diffColor(t, y)}`}>{diff(t, y)} vs yday</span>
            </div>
          </div>
        ))}
      </div>

      {/* Activity by Date */}
      {dateGroups.map(([date, items]) => (
        <div key={date} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-800">{formatDate(date)}</h4>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>{items.length} leads</span>
              <span>POCs: {sumField(items, 'poc_shortlisted')}</span>
              <span>Emails: {sumField(items, 'email_sent')}</span>
            </div>
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-[10px] text-gray-500 uppercase">
                <th className="px-4 py-1.5 text-left font-medium">Company</th>
                <th className="px-3 py-1.5 text-left font-medium">BDE</th>
                <th className="px-3 py-1.5 text-left font-medium">Location</th>
                <th className="px-3 py-1.5 text-center font-medium">POCs</th>
                <th className="px-3 py-1.5 text-center font-medium">Emails</th>
                <th className="px-3 py-1.5 text-center font-medium">Conn.</th>
                <th className="px-3 py-1.5 text-center font-medium">Replies</th>
                <th className="px-3 py-1.5 text-left font-medium">Stage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((a, i) => (
                <tr key={i} className="hover:bg-gray-50 text-sm">
                  <td className="px-4 py-1.5 font-medium text-gray-900 truncate max-w-[200px]">{a.company_name}</td>
                  <td className="px-3 py-1.5 text-gray-600 text-xs">{a.bde_name || '-'}</td>
                  <td className="px-3 py-1.5 text-gray-500 text-xs truncate max-w-[120px]">
                    {a.city && a.country ? `${a.city}, ${a.country}` : a.city || a.country || '-'}
                  </td>
                  <td className="px-3 py-1.5 text-center text-xs">{a.poc_shortlisted || 0}</td>
                  <td className="px-3 py-1.5 text-center text-xs">{a.email_sent || 0}</td>
                  <td className="px-3 py-1.5 text-center text-xs">{a.new_connection || 0}</td>
                  <td className="px-3 py-1.5 text-center text-xs">{a.auto_reply_received || 0}</td>
                  <td className="px-3 py-1.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">
                      {a.stage || 'New'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {activities.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-400 text-sm">No activities found for the selected filters</p>
        </div>
      )}
    </div>
  );
};

export default DailyActivityTracker;
